/**
 * SPA mirror of `marketing/lib/utm.ts` — campaign attribution capture (#3302).
 *
 * Same pattern as `src/lib/ad-consent.ts` and its marketing twin: both surfaces
 * read and write the SAME first-party cookies, scoped to `.franklyadvisors.com`,
 * so attribution captured on `www.franklyadvisors.com` is already visible here
 * after the marketing -> app subdomain hop. The marketing CTAs into the app
 * carry no query string, so the cookie IS the handoff — nothing else preserves
 * it across the boundary.
 *
 * This is a deliberate duplicate, not an oversight: the Vite app has no
 * `@frankly/shared` alias, and adding one is build-config risk. Drift is what
 * caused #3302 in the first place, so
 * `marketing/lib/__tests__/attribution-parity.test.ts` fails the build if the
 * cookie names, bag keys or source vocabulary diverge.
 *
 * NOT a replacement for `useTrackingParams` — that hook stays, because Meta CAPI
 * depends on its `_fbc` synthesis. This is additive.
 */

const BAG_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'fbclid',
  'gclid',
] as const;

const UTM_ONLY_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

type BagKey = (typeof BAG_KEYS)[number];

export type AttributionBag = Partial<Record<BagKey, string>>;
export type UtmOnly = Partial<Record<(typeof UTM_ONLY_KEYS)[number], string>>;

export interface Attribution {
  utm_first_touch: AttributionBag | null;
  utm_last_touch: AttributionBag | null;
  landing_page: string | null;
  referrer: string | null;
}

const COOKIE_FIRST = 'fba_utm';
const COOKIE_LAST = 'fba_utm_last';
// `_v2` — see the note in marketing/lib/utm.ts. The pre-#3302 sentinel bug left
// returning visitors holding a landing page that is actually the last page they
// viewed; renaming clears that population rather than freezing it.
const COOKIE_LANDING = 'fba_landing_v2';
const COOKIE_REFERRER = 'fba_referrer_v2';
const COOKIE_MAX_AGE = 90 * 24 * 60 * 60;

const MAX_VALUE_LEN = 200;

/** Must equal marketing's `ATTRIBUTION_CONTRACT`. Asserted by the parity test. */
export const ATTRIBUTION_CONTRACT = {
  bagKeys: BAG_KEYS,
  utmOnlyKeys: UTM_ONLY_KEYS,
  cookies: {
    first: COOKIE_FIRST,
    last: COOKIE_LAST,
    landing: COOKIE_LANDING,
    referrer: COOKIE_REFERRER,
  },
  sources: [
    'unknown',
    'cold_email',
    'paid_meta',
    'paid_google',
    'other',
    'direct',
    'organic',
    'referral',
  ],
  taggedSources: ['cold_email', 'paid_meta', 'paid_google', 'other'],
} as const;

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = name + '=';
  for (const c of document.cookie.split('; ')) {
    if (c.startsWith(prefix)) return decodeURIComponent(c.slice(prefix.length));
  }
  return null;
}

/**
 * Build the cookie string. Both conditional attributes are independent, and
 * getting either wrong silently drops the cookie rather than erroring:
 * a `Domain` the host isn't under is rejected per RFC 6265 §5.3, and `Secure`
 * on plain http is discarded. Exported so both are unit-tested directly.
 */
export function buildCookieString(
  name: string,
  value: string,
  hostname: string,
  protocol: string,
): string {
  const underApex =
    hostname === 'franklyadvisors.com' || hostname.endsWith('.franklyadvisors.com');
  const domain = underApex ? '; Domain=.franklyadvisors.com' : '';
  const secure = protocol === 'https:' ? '; Secure' : '';
  return `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax${domain}${secure}`;
}

function setCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return;
  document.cookie = buildCookieString(
    name,
    value,
    window.location.hostname,
    window.location.protocol,
  );
}

/** Pure parser — exported for tests. */
export function parseBagFromSearch(search: string): AttributionBag | null {
  const params = new URLSearchParams(search);
  const bag: AttributionBag = {};
  let hasAny = false;
  for (const key of BAG_KEYS) {
    const val = params.get(key);
    if (val && val.trim()) {
      bag[key] = val.trim().slice(0, MAX_VALUE_LEN);
      hasAny = true;
    }
  }
  return hasAny ? bag : null;
}

function readBagCookie(name: string): AttributionBag | null {
  const raw = getCookie(name);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return Object.keys(parsed as object).length ? (parsed as AttributionBag) : null;
  } catch {
    return null;
  }
}

/**
 * Reduce a referrer to its bare hostname.
 *
 * `classify_traffic_source` documents that it expects a host-only referrer
 * ("already stripped by the edge function") and anchors its search-engine regex
 * on `(^|\.)`. Handing that regex a full URL breaks the anchor in both
 * directions: `https://google.com/` stops matching (only `/` precedes
 * `google.`), while `https://l.facebook.com/l.php?u=...google.com...` starts
 * matching on a substring buried in the query string — classifying a Facebook
 * click as organic search. Normalising here is what keeps the JS and SQL
 * classifications identical.
 */
export function referrerHost(referrer?: string | null): string {
  if (!referrer) return '';
  try {
    return new URL(referrer).hostname.toLowerCase();
  } catch {
    // Unparseable. Return empty rather than the raw string: passing a non-host
    // to the host-anchored regex is the bug this function exists to prevent,
    // and `document.referrer` is always absolute-or-empty, so this branch means
    // the input was never a referrer in the first place.
    return '';
  }
}

/** True when the referrer is one of our own hosts — an internal hop, not a referral. */
export function isInternalReferrer(referrer: string): boolean {
  const host = referrerHost(referrer);
  return host === 'franklyadvisors.com' || host.endsWith('.franklyadvisors.com');
}

/**
 * Capture attribution from the current URL. Call once per page load.
 *
 * First touch is written once and never overwritten — that is the whole reason
 * both columns exist. Landing page and referrer have their OWN sentinel, so an
 * untagged visitor doesn't rewrite them on every navigation.
 */
export function captureAttribution(): void {
  if (typeof window === 'undefined') return;
  const bag = parseBagFromSearch(window.location.search);

  if (bag) {
    if (!getCookie(COOKIE_FIRST)) setCookie(COOKIE_FIRST, JSON.stringify(bag));
    setCookie(COOKIE_LAST, JSON.stringify(bag));
  }

  if (!getCookie(COOKIE_LANDING)) {
    // Capped to the server's own bound (z.string().max(500) on every
    // seller-intake schema). Uncapped, a single visit to a >500-char path
    // would poison this cookie for 90 days and 400 every later submit from
    // that browser.
    setCookie(COOKIE_LANDING, window.location.pathname.slice(0, 500));
    const ref = typeof document !== 'undefined' ? document.referrer : '';
    if (ref && !isInternalReferrer(ref)) {
      setCookie(COOKIE_REFERRER, ref.slice(0, 2048));
    }
  }
}

/**
 * Read persisted attribution, shaped for an `inquiries` insert.
 *
 * Every field is `null` when absent — **never `{}`**. #3302's negative control
 * turns on absent and empty staying distinguishable once the row is written.
 */
export function getAttribution(): Attribution {
  return {
    utm_first_touch: readBagCookie(COOKIE_FIRST),
    utm_last_touch: readBagCookie(COOKIE_LAST),
    landing_page: getCookie(COOKIE_LANDING),
    referrer: getCookie(COOKIE_REFERRER),
  };
}

/**
 * Narrow a bag to the five `utm_*` keys, for backends whose zod schema is
 * `.strict()` and would 400 on `fbclid`/`gclid`. Returns `null` when empty so
 * callers keep sending `null` rather than `{}`.
 */
export function pickUtmOnly(bag: AttributionBag | null): UtmOnly | null {
  if (!bag) return null;
  const out: UtmOnly = {};
  for (const key of UTM_ONLY_KEYS) {
    const v = bag[key];
    if (v) out[key] = v;
  }
  return Object.keys(out).length ? out : null;
}

const META_SOURCES = ['facebook', 'fb', 'meta', 'instagram', 'ig'];
const GOOGLE_SOURCES = ['google', 'adwords', 'google_ads'];
const SEARCH_ENGINE_RE =
  /(^|\.)(google|bing|yahoo|duckduckgo|ecosia|brave|baidu|yandex|ask|aol|startpage|qwant)\./;

/**
 * Derive the `inquiries.source` bucket. Mirrors `public.classify_traffic_source`
 * (migration 20260621042402) branch for branch, plus `unknown`, which the SQL
 * has no twin for.
 *
 * `unknown` is a FIRST-POSITION guard and the position is load-bearing: placed
 * last it is unreachable, because `no referrer -> direct` already swallows the
 * no-capture case — which is exactly the conflation #3302 was filed about.
 *
 * Total by construction — `inquiries.source` is NOT NULL, so a null or empty
 * return would drop the lead with a 23502.
 */
export function deriveSource(
  bag: AttributionBag | null,
  referrer?: string | null,
  landingPage?: string | null,
): string {
  if (!bag && !landingPage) return 'unknown';

  const source = (bag?.utm_source ?? '').toLowerCase();
  const medium = (bag?.utm_medium ?? '').toLowerCase();
  // Normalise to a host before the host-anchored regex sees it.
  //
  // This makes the two functions agree on the BUCKET for a given visit, not on
  // their input format. `classify_traffic_source` takes a bare host (the edge
  // function strips it via `normalizeReferrer`); `referrerHost` takes an
  // absolute URL and returns '' for a bare host. They are fed by disjoint
  // paths -- SQL over funnel_events/listing_page_views, this over inquiries --
  // so they never see the same value. Do not "fix parity" by handing the SQL a
  // URL or handing this a bare host; both break. See #3348.
  const host = referrerHost(referrer);

  if (medium === 'email') return 'cold_email';
  if (bag?.fbclid || META_SOURCES.includes(source)) return 'paid_meta';
  if (bag?.gclid || GOOGLE_SOURCES.includes(source)) return 'paid_google';
  if (source) return 'other';
  if (!host) return 'direct';
  if (SEARCH_ENGINE_RE.test(host)) return 'organic';
  return 'referral';
}

/** True when the bucket means "a campaign tagged this visit". */
export function isTaggedSource(source: string): boolean {
  return (ATTRIBUTION_CONTRACT.taggedSources as readonly string[]).includes(source);
}
